import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Search } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT } from "../data";
import { HelpSearch } from "./HelpSearch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/senior-tech-help", label: "Seniors" },
  { to: "/about", label: "About" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/faq", label: "FAQ" },
  { to: "/glossary", label: "Glossary" },
  { to: "/blog", label: "Blog" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const FindHelpDialog = (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent className="max-w-2xl bg-card border-border" data-testid="help-search-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold text-2xl text-foreground">
            What do you need help with?
          </DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground -mt-2 mb-1">
          Type it, or tap the microphone and just say it — like “help setting up wifi”.
        </p>
        <HelpSearch variant="modal" onSelect={() => setSearchOpen(false)} />
      </DialogContent>
    </Dialog>
  );

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b border-border">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
        <Logo />

        <div className="hidden lg:flex items-center gap-0.5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `px-3 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-accent bg-accent/10"
                    : "text-slate-300 hover:text-accent hover:bg-muted"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <button
            type="button"
            data-testid="nav-search-toggle"
            onClick={() => setSearchOpen(true)}
            aria-label="Find help"
            className="grid place-items-center h-11 w-11 rounded-full border border-border text-foreground hover:border-accent hover:text-accent transition-colors"
          >
            <Search className="h-5 w-5" />
          </button>
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-call-now"
            className="inline-flex items-center gap-2 text-foreground font-bold whitespace-nowrap hover:text-accent transition-colors"
          >
            <Phone className="h-4 w-4" /> {CONTACT.phone}
          </a>
          <Link
            to="/contact"
            data-testid="nav-book-appointment"
            className="inline-flex items-center h-12 px-5 rounded-full bg-accent text-white font-bold whitespace-nowrap hover:bg-[hsl(196,82%,27%)] transition-colors shadow-sm"
          >
            Book Now
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button
            type="button"
            data-testid="mobile-search-toggle"
            onClick={() => setSearchOpen(true)}
            aria-label="Find help"
            className="grid place-items-center h-12 w-12 rounded-xl border border-border text-foreground"
          >
            <Search className="h-6 w-6" />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid place-items-center h-12 w-12 rounded-xl border border-border text-foreground"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {FindHelpDialog}

      {open && (
        <div className="lg:hidden border-t border-border bg-card px-5 py-4 space-y-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `block px-4 py-3.5 rounded-xl text-lg font-semibold ${
                  isActive ? "text-accent bg-accent/10" : "text-foreground hover:bg-muted"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-3 space-y-2">
            <a
              href={CONTACT.phoneHref}
              data-testid="mobile-call-now"
              className="flex items-center justify-center gap-2 h-14 rounded-xl border-2 border-primary text-foreground font-bold text-lg"
            >
              <Phone className="h-5 w-5" /> Call {CONTACT.phone}
            </a>
            <Link
              to="/contact"
              data-testid="mobile-book-appointment"
              className="flex items-center justify-center h-14 rounded-xl bg-accent text-white font-bold text-lg"
            >
              Book an Appointment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
